import React from "react";
import {
  getVietmapPlaceDetail,
  hasVietmapServiceKey,
  searchVietmapAddressSuggestions,
  type VietmapAddressSuggestion,
} from "services/vietmapService";
import { useLanguage } from "context/LanguageContext";
import { toFriendlyError } from "../utils/sessionHelpers";

type UseVenueAddressSuggestionsOptions = {
  enabled: boolean;
  keyword: string;
  latitude: number;
  longitude: number;
  onSuggestionPreview: (suggestion: VietmapAddressSuggestion) => void;
  onSuggestionResolved: (payload: {
    address: string;
    latitude: number;
    longitude: number;
  }) => void;
};

export const useVenueAddressSuggestions = ({
  enabled,
  keyword,
  latitude,
  longitude,
  onSuggestionPreview,
  onSuggestionResolved,
}: UseVenueAddressSuggestionsOptions) => {
  const { t } = useLanguage();
  const [addressSuggestions, setAddressSuggestions] = React.useState<
    VietmapAddressSuggestion[]
  >([]);
  const [addressSuggestLoading, setAddressSuggestLoading] =
    React.useState(false);
  const [addressSuggestError, setAddressSuggestError] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    if (!enabled) {
      setAddressSuggestions([]);
      setAddressSuggestError(null);
      setAddressSuggestLoading(false);
      return;
    }

    const normalizedKeyword = keyword.trim();
    if (!hasVietmapServiceKey || normalizedKeyword.length < 2) {
      setAddressSuggestions([]);
      setAddressSuggestError(null);
      setAddressSuggestLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setAddressSuggestLoading(true);
      setAddressSuggestError(null);
      try {
        const suggestions = await searchVietmapAddressSuggestions(
          normalizedKeyword,
          {
            signal: controller.signal,
            limit: 6,
            focus: {
              latitude,
              longitude,
            },
          }
        );
        setAddressSuggestions(suggestions);
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        setAddressSuggestions([]);
        setAddressSuggestError(
          toFriendlyError(error, t("venue.searchFailed"), t)
        );
      } finally {
        setAddressSuggestLoading(false);
      }
    }, 300);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [enabled, keyword, latitude, longitude, t]);

  const handleSelectAddressSuggestion = React.useCallback(
    async (suggestion: VietmapAddressSuggestion) => {
      onSuggestionPreview(suggestion);
      setAddressSuggestions([]);
      setAddressSuggestError(null);
      setAddressSuggestLoading(true);

      try {
        const detail = await getVietmapPlaceDetail(suggestion.refId);
        onSuggestionResolved({
          address: detail.display || suggestion.display,
          latitude: Number(detail.lat.toFixed(6)),
          longitude: Number(detail.lng.toFixed(6)),
        });
      } catch (error: unknown) {
        setAddressSuggestError(
          toFriendlyError(error, t("venue.resolveFailed"), t)
        );
      } finally {
        setAddressSuggestLoading(false);
      }
    },
    [onSuggestionPreview, onSuggestionResolved, t]
  );

  return {
    addressSuggestions,
    addressSuggestLoading,
    addressSuggestError,
    handleSelectAddressSuggestion,
  };
};
